import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'

import { POST, httpOk } from 'helpers/api'

/**
 * MIJOZLARGA XABAR YUBORISH — umumiy modal.
 *
 * ⚠️ IKKI EKRANDA ISHLATILADI, shuning uchun alohida komponent:
 *    · Ma'lumotnoma → Mijozlar  (`/clients`)
 *    · Hisobotlar → Mijozlar    (`/report-clients`) — qarz yoshi bo'yicha
 *      aniq nishonga yuborish uchun eng qulay joy.
 *    Ikki nusxa kod bo'lsa, biri tuzatilib ikkinchisi eskirib qolardi.
 *
 * `clientIds` bo'sh bo'lsa — xabar HAMMA mijozga ketadi. Buni yashirmaymiz:
 * narx hisobi «1013 kishi / 2024 SMS» deb ochiq ko'rsatadi.
 */
function BroadcastModal({ show, onHide, clientIds = [] }) {
	const { t } = useTranslation()

	const [form, setForm] = useState({ message: '', channel: 'auto', is_ad: false })
	const [preview, setPreview] = useState(null)
	const [sending, setSending] = useState(false)
	const [progress, setProgress] = useState(null)

	useEffect(() => {
		if (show) {
			setForm({ message: '', channel: 'auto', is_ad: false })
			setPreview(null)
			setProgress(null)
			loadPreview('', 'auto')
		}
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	async function loadPreview(text, channel) {
		const res = await POST('/broadcast/preview', {
			text: text || '', channel: channel || 'auto', client_ids: clientIds,
		}, { loader: false })
		if (httpOk(res)) setPreview(res.data)
	}

	/* Yuborish BO'LAKLAB: server bir chaqiruvda 100 tadan yuboradi, biz
	   tugagunicha qayta chaqiramiz. Holat bazada — oyna yopilsa ham
	   yuborilganlar yo'qolmaydi. */
	async function send(e) {
		e.preventDefault()
		if (!form.message.trim()) return

		setSending(true)
		const created = await POST('/broadcast', {
			text: form.message,
			channel: form.channel,
			is_ad: !!form.is_ad,
			client_ids: clientIds,
		}, { loader: false })

		if (!httpOk(created)) { setSending(false); return }

		const id = created.data.id
		let guard = 0
		// eslint-disable-next-line no-constant-condition
		while (true) {
			const step = await POST(`/broadcast/${id}/process`, {}, { loader: false })
			if (!httpOk(step)) break
			setProgress({ sent: step.data.sent, left: step.data.left })
			if (step.data.done) break
			if (++guard > 200) break
		}

		setSending(false)
		onHide()

		/* ⚠️ SINOV REJIMIDA «Yuborildi» deyish CHALG'ITADI: bazaga yozuv tushadi,
		   lekin mijozga hech narsa bormaydi. Foydalanuvchi «xabar ketdi» deb
		   o'ylab yurmasin — rejimni ochiq aytamiz. */
		if (preview?.test_mode) {
			toast.info('Sinov rejimi — yozib olindi, lekin mijozga YUBORILMADI. '
				+ 'Haqiqiy yuborish uchun: Sozlamalar → SMS integratsiya → sinov rejimini o\'chiring.')
		} else {
			toast.success(t('sent'))
		}
	}

	return (
		<Modal show={show} animation={false} centered
			dialogClassName="update-modal-width" backdrop="static" onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>
					{t('sms_sending')}
					{clientIds.length > 0 &&
						<span className="badge bg-primary ms-2">{clientIds.length}</span>}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<form onSubmit={send} autoComplete="off">

					<div className="form-group">
						<label>Kanal</label>
						<div className="btn-group w-100">
							{[
								{ k: 'auto', n: 'Avtomat (Telegram → SMS)' },
								{ k: 'telegram', n: 'Faqat Telegram' },
								{ k: 'sms', n: 'Faqat SMS' },
							].map(o => (
								<button type="button" key={o.k}
									className={'btn btn-sm ' + (form.channel === o.k ? 'btn-primary' : 'btn-outline-secondary')}
									onClick={() => { setForm({ ...form, channel: o.k }); loadPreview(form.message, o.k) }}>
									{o.n}
								</button>
							))}
						</div>
						<div className="text-muted fz-14 mt-1">
							Avtomat rejimda Telegrami borlarga <b>tekin</b> ketadi, qolganiga SMS.
						</div>
					</div>

					<div className="form-group">
						<label>{t('description')}</label>
						<textarea className="form-control" rows="4"
							value={form.message}
							onChange={e => setForm({ ...form, message: e.target.value })}
							onBlur={e => loadPreview(e.target.value, form.channel)} />
						<span className="text-muted fz-14">
							{t('letters')} — {form.message.length}
							{preview?.parts > 0 &&
								<> · <b>{preview.parts}</b> SMS/kishi ({preview.encoding})</>}
						</span>
					</div>

					{/* ═══ NARX — yuborishdan OLDIN ═══ */}
					{preview &&
						<div className="alert alert-light border py-2">
							<div className="d-flex justify-content-between">
								<span>Jami qabul qiluvchi</span><b>{preview.total}</b>
							</div>
							<div className="d-flex justify-content-between text-success">
								<span>Telegram (tekin)</span><b>{preview.telegram}</b>
							</div>
							<div className="d-flex justify-content-between">
								<span>SMS</span><b>{preview.sms}</b>
							</div>
							{preview.sms_total_parts > 0 &&
								<div className="d-flex justify-content-between border-top pt-1 mt-1">
									<span className="fw-bold">Jami SMS (qism)</span>
									<b className="text-danger">{preview.sms_total_parts}</b>
								</div>
							}
							{preview.skipped_optout > 0 &&
								<div className="text-muted fz-14 mt-1">
									{preview.skipped_optout} ta mijoz «STOP» yozgan — ularga yuborilmaydi.
								</div>
							}
							{preview.test_mode &&
								<div className="text-warning fz-14 mt-1">
									<i className="uil-exclamation-triangle me-1" />
									Sinov rejimi yoqiq — haqiqiy SMS ketmaydi (Sozlamalardan o'chiriladi).
								</div>
							}
							{!preview.configured &&
								<div className="text-danger fz-14 mt-1">
									SMS provayderi sozlanmagan — faqat Telegram ishlaydi.
								</div>
							}
						</div>
					}

					{progress &&
						<div className="alert alert-info py-2">
							Yuborilmoqda… qoldi: <b>{progress.left}</b>
						</div>
					}

					<div className="d-flex gap-2 w-100 mt-3">
						<button type="button" className="btn btn-outline-warning w-100" onClick={onHide}>
							{t('cancel')}
						</button>
						<button type="submit" className="btn btn-primary w-100"
							disabled={sending || !form.message.trim()}>
							{sending ? t('loading') : t('send')}
						</button>
					</div>
				</form>
			</Modal.Body>
		</Modal>
	)
}

export default BroadcastModal
